// ============================================
// FAQ 展开内容视图（Q/A/N 区块）
// 管理页和用户页共用
// ============================================

import { useState } from 'react';
import { Edit3, FileText, Download, Paperclip, Eye as EyeIcon } from 'lucide-react';
import FaqRichEditor from './FaqRichEditor';
import FaqHtmlSandboxPreview from './FaqHtmlSandboxPreview';
import FaqAttachmentPreview from './FaqAttachmentPreview';
import { uploadFaqImage } from './faq-service';
import { richTextHasContent } from './faq-rich-content';
import type { FaqItem, FaqAttachment } from './types';

interface FaqExpandedViewProps {
  item: FaqItem;
  isAdmin?: boolean;
  onEdit?: (item: FaqItem) => void;
}

export default function FaqExpandedView({ item, isAdmin, onEdit }: FaqExpandedViewProps) {
  const [previewAttachment, setPreviewAttachment] = useState<FaqAttachment | null>(null);
  const hasAttachments = item.attachments && item.attachments.length > 0;
  const hasNotes = richTextHasContent(item.notes);
  const isHtmlAnswer = item.answer_format === 'html';

  return (
    <div className="px-5 pb-5 border-t border-slate-100 space-y-4 pt-4">

      {/* 封面图 */}
      {item.cover_image && (
        <div className="bg-slate-50 rounded-xl overflow-hidden">
          <img src={item.cover_image} alt="封面" className="w-full max-h-[320px] object-contain" />
        </div>
      )}

      {/* ── A 回答 ── */}
      {item.answer && (
        <div className="rounded-xl border border-emerald-200/50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50/80 border-b border-emerald-100">
            <span className="w-5 h-5 rounded-md bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center">A</span>
            <span className="text-xs font-semibold text-emerald-600">回答</span>
          </div>
          <div className="px-4 py-4 bg-white">
            {isHtmlAnswer ? (
              <FaqHtmlSandboxPreview html={item.answer} minHeight="640px" />
            ) : (
              <FaqRichEditor content={item.answer} readOnly uploadImage={uploadFaqImage} minHeight="60px" />
            )}
          </div>
        </div>
      )}

      {/* ── N 附件与备注 ── */}
      {(hasNotes || hasAttachments) && (
        <div className="rounded-xl border border-amber-200/50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50/80 border-b border-amber-100">
            <span className="w-5 h-5 rounded-md bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">N</span>
            <span className="text-xs font-semibold text-amber-600">附件与备注</span>
          </div>
          <div className="px-4 py-4 bg-white space-y-4">
            {hasNotes && (
              <FaqRichEditor content={item.notes ?? undefined} readOnly uploadImage={uploadFaqImage} minHeight="40px" />
            )}

            {/* 附件列表 */}
            {hasAttachments && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-amber-500/80 uppercase tracking-wider flex items-center gap-1.5">
                  <Paperclip size={12} />
                  附件 ({item.attachments.length})
                </p>
                <div className="space-y-1">
                  {item.attachments.map((att, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 px-3 py-2 bg-amber-50/50 border border-amber-100 rounded-xl hover:bg-amber-50 hover:border-amber-200 transition-all group"
                    >
                      <FileText size={16} className="text-amber-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-600 truncate">{att.name}</p>
                        <p className="text-[10px] text-slate-400">{(att.size / 1024).toFixed(1)} KB · {att.type.split('/').pop()}</p>
                      </div>
                      <button
                        onClick={() => setPreviewAttachment(att)}
                        className="px-2.5 py-1 text-xs font-medium text-amber-600 bg-amber-100/60 hover:bg-amber-100 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <EyeIcon size={12} />
                        预览
                      </button>
                      <a
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-slate-400 hover:text-sky-500 rounded-lg transition-colors"
                        title="下载"
                      >
                        <Download size={14} />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 管理员编辑按钮 */}
      {isAdmin && onEdit && (
        <div className="pt-2">
          <button
            onClick={() => onEdit(item)}
            className="flex items-center gap-1.5 px-4 py-2 bg-sky-50 text-sky-600 text-xs font-semibold rounded-xl hover:bg-sky-100 transition-all"
          >
            <Edit3 size={13} />
            编辑此 FAQ
          </button>
        </div>
      )}

      {/* 附件预览弹窗 */}
      <FaqAttachmentPreview
        attachment={previewAttachment}
        isOpen={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}
